import React, { useState } from 'react';
import { Table, Typography, Button, Modal, Dropdown } from 'antd';
import { DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import PatientDocumentsEmptyState from "../document/PatientDocumentsEmptyState";
import ProfileModal from '../../components/ProfileModal';
import GenerateDocumentButton from "../patient/GenerateDocumentButton";
import LinkButton from '../buttons/LinkButton';
import deleteDocument from '../../utils/deleteDocument';

const { Text } = Typography;

export default function PatientDocumentList({ patient, documents, pagination = {}, onPageChange, onClick, onGenerateClicked }) {
  const { t } = useTranslation();
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const handleDelete = async (document) => {
    Modal.confirm({
      title: t('patient_documents.delete.title'),
      content: t('patient_documents.delete.confirm_message'),
      okText: t('patient_documents.delete.confirm_ok'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteDocument(document.getUUID());
          toast.success(t('patient_documents.delete.success'));
          onPageChange(pagination.current);
        } catch (error) {
          console.error(error);
          toast.error(t('patient_documents.delete.error'));
        }
      },
    });
  };

  const columns = [
    {
      title: '',
      dataIndex: 'doc_type',
      key: 'doc_type',
      width: '60%',
      render: (_, record) => {
        if (record.isReady() || record.isGenerating()) {
          return (
            <LinkButton onClick={() => onClick(record)}>
              {record.getTitle()}
            </LinkButton>
          );
        } else {
          return <Text type="danger">{record.getDisplayProblem()}</Text>;
        }
      },
    },
    {
      title: '',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: '30%',
      responsive: ['md'],
      render: (_, record) => (
        <Text type="secondary">
          {record.getCompletedAt()}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '10%',
      align: 'right',
      render: (_, record) => (
        record.isReady() && (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'delete',
                  label: t('patient_documents.delete.action'),
                  icon: <DeleteOutlined />,
                  onClick: () => handleDelete(record),
                  danger: true,
                },
              ],
            }}
            trigger={['hover']}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              style={{ color: 'grey' }}
            />
          </Dropdown>
        )
      ),
    }
  ];

  if (documents.length === 0) {
    return <PatientDocumentsEmptyState 
      patient={patient} 
      handleDocumentsReload={onPageChange}
      onGenerateClicked={onGenerateClicked}
    />;
  }

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 18, marginBottom: 18, gap: 8}}>
        <GenerateDocumentButton
          patient={patient}
          onGenerateClicked={onGenerateClicked}
        />
        <Button
          onClick={() => setProfileModalOpen(true)}>
          {t('patient_documents.edit_profile')}
        </Button>
      </div>

      <Table
        showHeader={false}
        columns={columns}
        dataSource={documents}
        rowKey={(record) => record.getUUID()}
        pagination={
          pagination?.total > pagination.pageSize
            ? { ...pagination, position: ['bottomCenter'] }
            : false
        }
        onChange={(pagination) => onPageChange(pagination.current)}
      />

      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        isEditing={true}
      />
    </div>
  );
}